import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Phone, MessageSquare, MapPin, Clock, Car, DollarSign, Camera } from 'lucide-react';
import '../styles/Home.css';

const Home = () => {
  const steps = [
    {
      number: '1',
      title: 'Text your order',
      description: 'Send your name, address, where you want food from, and your order.',
    },
    {
      number: '2',
      title: 'We pick it up',
      description: 'We pick up from the local spot you choose and head your way.',
    },
    {
      number: '3',
      title: 'Delivery to your door',
      description: 'You pay for your food plus delivery. No added fees.',
    },
  ];

  const pickupExamples = [
    'Local restaurants',
    'Taco trucks',
    'Fizzys Gourmet Soda',
    'Chick-fil-A food truck when available',
    'HTF Herbalife Nutrition',
  ];

  const deliveryAreas = [
    { city: 'Chiefland', fee: '$5' },
    { city: 'Trenton', fee: '$7' },
    { city: 'Cedar Key', fee: '$10' },
    { city: 'Otter Creek', fee: '$12' },
    { city: 'Old Town', fee: '$12' },
  ];

  const rideServices = [
    'Airport transportation',
    'Doctor appointments',
    'Scheduled rides',
  ];

  const orderChecklist = ['Name', 'Address', 'Restaurant or vendor', 'Order'];

  const localImages = [
    {
      src: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=900&q=80',
      alt: 'Coffee cups ready for pickup',
    },
    {
      src: '/fizzy.jpg',
      alt: "Fizzys soda drink",
    },
    {
      src: '/mcdonalds_converted.jpg',
      alt: "McDonald's meal",
    },
    {
      src: 'https://images.unsplash.com/photo-1513456852971-30c0b8199d4d?auto=format&fit=crop&w=900&q=80',
      alt: 'Tacos and casual truck-style food',
    },
  ];

  return (
    <div className="home-page">
      <section className="hero-section">
        <div className="container hero-shell">

          <div className="hero-wordmark">
            <div className="wordmark-main">Front Door</div>
            <div className="wordmark-divider">
              <span className="wordmark-line" />
              <span className="wordmark-diamond">◆</span>
              <span className="wordmark-sub">Service</span>
              <span className="wordmark-diamond">◆</span>
              <span className="wordmark-line" />
            </div>
          </div>

          <h1 className="hero-title">Hot food delivered from local spots. Just text your order.</h1>

          <p className="hero-subtitle">
            We pick up from local restaurants, food trucks, and vendors and deliver straight to your door.
          </p>

          <div className="no-fees-badge">No apps. No hidden fees. Just text and pay.</div>

          <div className="hero-cta">
            <a href="sms:3525353292" className="cta-btn cta-primary">Text Your Order</a>
            <a href="tel:3525353292" className="cta-btn cta-call">352-535-3292</a>
            <a href="https://cash.app/$DIBTHEMAN" target="_blank" rel="noopener noreferrer" className="cta-btn cta-cashapp">Pay via CashApp $DIBTHEMAN</a>
          </div>

          <div className="hero-response-time">Fast response, usually within minutes.</div>

          <div className="hero-giveaway-highlight">Win $100 every month just for ordering</div>

          <div className="hero-info-strip">
            <div className="info-strip-item">
              <span className="hero-card-label">Hours</span>
              <strong>Mon-Sat 8:30 AM – 8:30 PM</strong>
            </div>
            <div className="info-strip-divider" />
            <div className="info-strip-item">
              <span className="hero-card-label">Order</span>
              <strong>Text 352-535-3292</strong>
            </div>
            <div className="info-strip-divider" />
            <div className="info-strip-item">
              <span className="hero-card-label">Pay</span>
              <strong>CashApp $DIBTHEMAN</strong>
            </div>
            <div className="info-strip-divider" />
            <div className="info-strip-item">
              <span className="hero-card-label">Starting fee</span>
              <strong>Chiefland $5</strong>
            </div>
          </div>

          <div className="hero-order-example">
            <div className="hero-order-title">Text this:</div>
            <ul className="hero-order-list">
              {orderChecklist.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

        </div>
      </section>

      <section className="section how-it-works">
        <div className="container">
          <div className="section-heading">
            <span className="section-kicker">How It Works</span>
            <h2 className="section-title">Order by text in three quick steps.</h2>
          </div>

          <div className="steps-grid">
            {steps.map((step) => (
              <Card key={step.number} className="step-card">
                <CardContent className="step-content">
                  <div className="step-number">{step.number}</div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="section pickup-section">
        <div className="container pickup-shell">
          <div className="pickup-copy">
            <span className="section-kicker">Where We Pick Up From</span>
            <h2 className="section-title">Local pickup options people already know.</h2>
            <p className="section-copy">
              We pick up from local restaurants, food trucks, and vendors around Chiefland and surrounding areas.
            </p>
          </div>

          <Card className="pickup-card">
            <CardContent className="pickup-card-content">
              <div className="pickup-card-top">
                <div className="pickup-image-stack">
                  <img src={localImages[1].src} alt={localImages[1].alt} className="pickup-image pickup-image-large" />
                  <img src={localImages[2].src} alt={localImages[2].alt} className="pickup-image pickup-image-small" />
                  <img src={localImages[3].src} alt={localImages[3].alt} className="pickup-image pickup-image-wide" />
                </div>
                <div className="pickup-image-note">
                  <Camera className="pickup-image-note-icon" />
                  <span>Simple pickup from local spots and vendors.</span>
                </div>
              </div>
              <ul className="pickup-list">
                {pickupExamples.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="section delivery-section">
        <div className="container delivery-shell">
          <div className="delivery-copy">
            <span className="section-kicker">Delivery Areas and Fees</span>
            <h2 className="section-title">Straightforward pricing.</h2>
            <p className="section-copy">
              No added fees. You only pay for your food plus delivery.
            </p>
          </div>

          <div className="delivery-grid">
            {deliveryAreas.map((area) => (
              <Card key={area.city} className="delivery-card">
                <CardContent className="delivery-content">
                  <MapPin className="delivery-icon" />
                  <h3 className="delivery-city">{area.city}</h3>
                  <div className="delivery-fee">{area.fee}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="section giveaway-section">
        <div className="container">
          <Card className="giveaway-card">
            <CardContent className="giveaway-card-content">
              <span className="section-kicker">Monthly Giveaway</span>
              <h2 className="giveaway-title">Monthly Giveaway</h2>
              <p className="giveaway-copy">
                Every order you place enters you into a monthly drawing. One customer wins $100 cash at the end of each month.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="section rides-section">
        <div className="container rides-shell">
          <div className="rides-copy">
            <span className="section-kicker">Transportation Services</span>
            <h2 className="section-title">Need a ride? Text to schedule.</h2>
            <p className="section-copy">Rides are by appointment only.</p>
          </div>

          <Card className="rides-card">
            <CardContent className="rides-card-content">
              <ul className="rides-list">
                {rideServices.map((item) => (
                  <li key={item}>
                    <Car className="rides-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a href="sms:3525353292" className="cta-btn cta-secondary rides-cta">Text to schedule your ride</a>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="section contact-section">
        <div className="container">
          <div className="contact-card">
            <div className="contact-copy">
              <span className="section-kicker">Contact / Order Now</span>
              <h2 className="section-title">Ready to order?</h2>
              <p className="section-copy">Text us your:</p>
              <ul className="contact-list">
                {orderChecklist.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="contact-actions">
              <a href="tel:3525353292" className="contact-phone">352-535-3292</a>
              <a href="sms:3525353292" className="cta-btn cta-primary">Text Your Order</a>
              <a href="https://cash.app/$DIBTHEMAN" target="_blank" rel="noopener noreferrer" className="contact-cashapp contact-cashapp-link">
                <span>CashApp</span>
                <strong>$DIBTHEMAN</strong>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="trust-footer-section">
        <div className="container">
          <Card className="about-trust-card">
            <CardContent className="about-trust-content">
              <p className="about-trust-text">
                Front Door Service LLC is a local, community-first delivery service proudly serving the
                Tri-County Area. We believe in honest service, fair pricing, and helping our neighbors
                get the food they love without the drive.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <footer className="site-footer">
        <div className="container">
          <div className="site-footer-content">
            <h3 className="site-footer-title">Front Door Service LLC</h3>
            <p>Tri-County Area, Florida</p>
            <a href="tel:3525353292" className="site-footer-link">352-535-3292</a>
            <p>Mon-Sat 8:30 AM-8:30 PM</p>
            <a href="https://cash.app/$DIBTHEMAN" target="_blank" rel="noopener noreferrer" className="site-footer-link">
              $DIBTHEMAN
            </a>
            <div className="site-footer-quote">
              "Greater is He that is in me, than he that is in the world."
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
